// src/pages/creator-panel/CallLogs/tabs/RagLogTab.js
// Raw per-session retrieval log viewer for the Call Logs "Vector DB" / "Entity"
// tabs. One component, parameterized by `logType`.
//
// These are .log files klonit writes per RAG turn (what it pulled from the
// vector DB / entity-knowledge store), shipped to GCS and proxied back inline
// by klonit-api — the same delivery model as the Chat Analysis "Logs" button
// (ChatAnalysis/SessionLogsDrawer.js), which this is adapted from.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, TextField, InputAdornment, IconButton, Button, CircularProgress,
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../../utils/axiosInstance';

const RagLogTab = ({ sessionId, logType, label }) => {
    // meta: { exists, content, truncated, size_bytes, last_uploaded_at }
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');

    const fetchLogs = useCallback(async () => {
        if (!sessionId || !logType) return;
        setError(null);
        setLoading(true);
        try {
            // Content comes back inline — klonit-api proxies it from GCS so the
            // browser never makes a cross-origin fetch (no per-bucket CORS).
            const res = await axiosInstance.get(
                `/api/call-sessions/${sessionId}/rag-logs`,
                { params: { log_type: logType } }
            );
            setMeta(res.data || null);
        } catch (e) {
            setError(e?.response?.data?.message || e.message || 'Failed to load logs.');
            setMeta(null);
        } finally {
            setLoading(false);
        }
    }, [sessionId, logType]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const filteredText = useMemo(() => {
        const text = meta?.content || '';
        if (!text) return '';
        const q = search.trim().toLowerCase();
        if (!q) return text;
        return text
            .split('\n')
            .filter((line) => line.toLowerCase().includes(q))
            .join('\n');
    }, [meta?.content, search]);

    const sizeKB = meta?.size_bytes ? (meta.size_bytes / 1024).toFixed(1) : null;

    const handleDownload = useCallback(() => {
        // Files are tiny (a few KB) and always inline, so build the blob from
        // the in-memory content — no extra round-trip / download endpoint.
        if (!meta?.content) return;
        const blob = new Blob([meta.content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${logType}-${sessionId}.log`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [meta?.content, logType, sessionId]);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder={`Filter ${label} log lines`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    disabled={!meta?.content}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                    }}
                    sx={{ minWidth: 240, flex: 1 }}
                />
                <IconButton size="small" onClick={fetchLogs} disabled={loading} title="Refresh">
                    <RefreshIcon fontSize="small" />
                </IconButton>
                {meta?.exists && meta?.content && (
                    <IconButton size="small" onClick={handleDownload} title="Download .log">
                        <DownloadIcon fontSize="small" />
                    </IconButton>
                )}
                {meta?.exists && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontWeight: 600 }}>
                        {sizeKB ? `${sizeKB} KB` : ''}
                    </Typography>
                )}
            </Box>

            <Box sx={{
                maxHeight: 500, overflowY: 'auto',
                border: '1px solid', borderColor: 'divider', borderRadius: 1,
                p: 2,
            }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {!loading && error && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>
                        <Button size="small" variant="outlined" onClick={fetchLogs} startIcon={<RefreshIcon />}>
                            Retry
                        </Button>
                    </Box>
                )}

                {!loading && !error && meta && meta.exists === false && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {meta.message ||
                                'No logs uploaded yet. Logs are synced every 5 minutes — try again shortly.'}
                        </Typography>
                        <Button size="small" variant="outlined" onClick={fetchLogs} startIcon={<RefreshIcon />}>
                            Check again
                        </Button>
                    </Box>
                )}

                {!loading && !error && meta?.exists && meta?.truncated && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            This log file is too large to preview inline ({sizeKB} KB).
                        </Typography>
                    </Box>
                )}

                {!loading && !error && meta?.exists && !meta?.truncated && (
                    <Box
                        component="pre"
                        sx={{
                            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                            fontSize: 12,
                            lineHeight: 1.5,
                            m: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            color: 'text.primary',
                        }}
                    >
                        {filteredText || (
                            <Typography variant="body2" color="text.secondary">
                                No lines match this filter.
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default RagLogTab;
